import { AxiosResponse } from 'axios';

import { KeyValue } from '../models/common';
import { PaginationResponse } from '../models/common/paginationResponse';
import { Result } from '../models/common/resultResponse';
import { RemoveQuoteItemRequest } from '../models/request';
import {
  OrderCancelRequest,
  OrderSaveRequest
} from '../models/request/orderRequest';
import {
  ApproveOrRejectQuoteRequest,
  GetAllClaimImagesResquest,
  GetItemsToRequoteRequest,
  GetQuoteItemRequest,
  ImportQuoteFileRequest,
  PreQuoteRequest,
  QuoteCartsAnswerPendingRequest,
  QuoteCheckoutRequest,
  QuoteClaimNumberValidationRequest,
  QuoteCountStatusRequest,
  QuoteFilterRequest,
  QuoteGetItemsFilterRequest,
  QuoteRemoveQuoteItemRequest,
  QuoteSaveIntegrationRequest,
  QuoteSelectBestSupplierRequest,
  QuoteSelectSuppliersDataRequest,
  QuoteSendSuppliersZeroInventoryEmailRequest,
  QuoteXmlRequest,
  RequoteNationalRequest,
  RequoteRequest,
  SaveQuoteImagesRequest,
  SaveXmlPaintRequest
} from '../models/request/quoteRequest';
import {
  AwaitingReleaseApprovalResponse,
  CartResponseCount,
  ClaimDetailsResponse,
  GetItemsToRequoteResponse,
  GetQuoteItemResponse,
  ImportQuoteFileResponse,
  PreQuoteResponse,
  QuoteAwaitingApprovalResponse,
  QuoteAwaitingDetailResponse,
  QuoteCartsAnswerPendingResponse,
  QuoteCheckoutResponse,
  QuoteDataResponse,
  QuoteDirectAssignItemsResponse,
  QuoteGetItemConditionsWrapper,
  QuoteHeaderAnswerInformationResponse,
  QuoteImageResponse,
  QuoteManualChekoutResponse,
  QuoteResponse,
  QuoteResponseClaimNumberValidation,
  QuoteResponseCount,
  QuoteSaveResponse,
  QuoteSelectBestSupplierResponse,
  QuoteSelectSuppliersDataResponseItems,
  QuoteTotalLossResponse,
  QuoteXmlResponse
} from '../models/response';
import { TimeoutRequestConfigurationEnum } from '../services/enum/TimeoutRequestConfigurationEnum';
import { api } from './base/api';
import { apiFetch } from './base/apiFetch';
import { IQuoteApi } from './base/quoteApi';

const baseRoute: string = 'Quote';

export class QuoteApi extends IQuoteApi {
  private activeAbortController: AbortController | null = null;

  getSuppliersForTotalLoss(
    request: QuoteSelectSuppliersDataRequest
  ): Promise<AxiosResponse<Result<QuoteSelectSuppliersDataResponseItems>>> {
    return api.get(`${baseRoute}/getsuppliersfortotalloss`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.FiveMinutesInMilliseconds
    });
  }
  getDirectAssignItems(
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteDirectAssignItemsResponse[]>>> {
    return api.get(`${baseRoute}/getdirectassignitems?quoteGuid=${guidQuote}`);
  }

  getClaimDirectAssignItems(
    guidClaim: string
  ): Promise<AxiosResponse<Result<QuoteDirectAssignItemsResponse[]>>> {
    return api.get(
      `${baseRoute}/getclaimdirectassignitems?claimGuid=${guidClaim}`
    );
  }

  detailAwaiting(
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteAwaitingDetailResponse>>> {
    return api.get(`${baseRoute}/detailawaiting/${guidQuote}`);
  }
  getDomain(
    isUserControl?: boolean | null
  ): Promise<AxiosResponse<Result<KeyValue[]>>> {
    if (isUserControl === null || isUserControl === undefined) {
      return api.get(`${baseRoute}/domain`);
    }

    return api.get(`${baseRoute}/domain`, {
      params: { isUserControl }
    });
  }

  getValidClaimNumber(
    request: QuoteClaimNumberValidationRequest
  ): Promise<AxiosResponse<QuoteResponseClaimNumberValidation>> {
    return api.get(`${baseRoute}/ValidClaimNumber`, { params: request });
  }

  async getSearchGrid(
    request: QuoteFilterRequest
  ): Promise<AxiosResponse<PaginationResponse<QuoteDataResponse>>> {
    if (this.activeAbortController) {
      this.activeAbortController.abort();
    }
    this.activeAbortController = new AbortController();
    const response = await api.post(`${baseRoute}`, request, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds,
      signal: this.activeAbortController.signal
    });
    this.activeAbortController = null;
    return response;
  }

  saveQuote(
    request: OrderSaveRequest
  ): Promise<AxiosResponse<Result<QuoteSaveResponse>>> {
    return api.post(`${baseRoute}/save`, request);
  }

  updateQuote(
    request: OrderSaveRequest
  ): Promise<AxiosResponse<Result<QuoteSaveResponse>>> {
    return api.post(`${baseRoute}/update`, request);
  }

  getQuoteById(
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteResponse>>> {
    return api.get(`${baseRoute}/${guidQuote}`);
  }

  cancelQuoteById(
    request: OrderCancelRequest
  ): Promise<AxiosResponse<Result<any>>> {
    return api.delete(
      `${baseRoute}/cancel/${request.guid}/${encodeURIComponent(request.route)}`
    );
  }
  countProcurementStatus(
    request: QuoteCountStatusRequest
  ): Promise<AxiosResponse<Result<QuoteResponseCount[]>>> {
    return api.get(`${baseRoute}/countProcurementStatus`, { params: request });
  }

  getSelectSuppliers(
    request: QuoteSelectSuppliersDataRequest
  ): Promise<AxiosResponse<Result<QuoteSelectSuppliersDataResponseItems>>> {
    return api.get(`${baseRoute}/selectsuppliers`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }
  getSupplierItems(
    guidQuoteItem: string
  ): Promise<AxiosResponse<Result<QuoteSelectSuppliersDataResponseItems>>> {
    return api.get(`${baseRoute}/getSupplierItems`, {
      params: { guidQuoteItem },
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }

  getSummary(
    request: QuoteFilterRequest
  ): Promise<AxiosResponse<PaginationResponse<QuoteDataResponse>>> {
    return api.get(`${baseRoute}/summary`, { params: request });
  }

  getCartsPendingToAnswer(
    request: QuoteCartsAnswerPendingRequest
  ): Promise<
    AxiosResponse<PaginationResponse<QuoteCartsAnswerPendingResponse>>
  > {
    return api.get(`${baseRoute}/SearchCartsPending`, { params: request });
  }

  getSelectBestSupplier(
    request: QuoteSelectBestSupplierRequest
  ): Promise<AxiosResponse<Result<QuoteSelectBestSupplierResponse>>> {
    return api.get(`${baseRoute}/SelectBestSupplier`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.OneMinuteInMilliseconds
    });
  }

  getItems(
    request: QuoteGetItemsFilterRequest
  ): Promise<AxiosResponse<Result<QuoteGetItemConditionsWrapper>>> {
    return api.get(`${baseRoute}/getStepThreeItems`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.OneMinuteInMilliseconds
    });
  }

  headeranswerinformation(
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteHeaderAnswerInformationResponse>>> {
    return api.get(`${baseRoute}/headeranswerinformation/${guidQuote}`);
  }

  countCartStatus(
    request: QuoteCountStatusRequest
  ): Promise<AxiosResponse<Result<CartResponseCount[]>>> {
    return api.get(`${baseRoute}/SearchCartsPendingCount`, { params: request });
  }

  validateTotalLoss(
    guidQuote: string
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.get(`${baseRoute}/validateTotalLoss?guidQuote=${guidQuote}`);
  }

  validateawaitingreleaseaprroval(
    guidQuote: string
  ): Promise<AxiosResponse<Result<AwaitingReleaseApprovalResponse>>> {
    return api.get(
      `${baseRoute}/validateawaitingreleaseaprroval?guidQuote=${guidQuote}`
    );
  }

  checkout(
    request: QuoteCheckoutRequest
  ): Promise<AxiosResponse<Result<QuoteCheckoutResponse>>> {
    return api.post(`${baseRoute}/checkout`, request, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }

  getSendSuppliersZeroInventoryEmail(
    request: QuoteSendSuppliersZeroInventoryEmailRequest
  ): Promise<AxiosResponse<Result<void>>> {
    return api.get(`${baseRoute}/sendSuppliersZeroInventoryEmail`, {
      params: request
    });
  }

  getPreQuote(
    guidQuote: string
  ): Promise<AxiosResponse<Result<PreQuoteResponse>>> {
    return api.get(`${baseRoute}/prequote/${guidQuote}`);
  }

  savePreQuote(
    request: PreQuoteRequest
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.post(`${baseRoute}/prequote/save`, request, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }

  saveIntegration(
    request: QuoteSaveIntegrationRequest[]
  ): Promise<AxiosResponse<Result<void>>> {
    return api.post(`${baseRoute}/generatequoteimport`, request, {
      timeout: TimeoutRequestConfigurationEnum.FiveMinutesInMilliseconds
    });
  }

  saveIntegrationPDF(files: File[]): Promise<AxiosResponse<Result<void>>> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }

    return api.post(`${baseRoute}/generatequoteimportpdf`, formData, {
      timeout: TimeoutRequestConfigurationEnum.FiveMinutesInMilliseconds
    });
  }

  getImportQuoteFile(
    request: ImportQuoteFileRequest
  ): Promise<AxiosResponse<Result<ImportQuoteFileResponse[]>>> {
    return api.get(`${baseRoute}/getimportquotefile`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.TwoMinutesInMilliseconds
    });
  }

  getAllClaimImages(
    request: GetAllClaimImagesResquest
  ): Promise<AxiosResponse<PaginationResponse<QuoteImageResponse>>> {
    return api.get(`${baseRoute}/getallclaimimages`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.TwoMinutesInMilliseconds
    });
  }

  saveQuoteImages(
    request: SaveQuoteImagesRequest
  ): Promise<AxiosResponse<Result<QuoteCheckoutResponse>>> {
    return api.post(`${baseRoute}/savequoteimage`, request);
  }

  deleteImageQuote(guid: string): Promise<AxiosResponse<Result<boolean>>> {
    return api.delete(`${baseRoute}/deleteimagequote`, { params: { guid } });
  }

  importQuoteFiles(files: File[]): Promise<AxiosResponse<Result<boolean>>> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('quoteFiles', file);
    }
    return api.post(`${baseRoute}/importquotefiles`, formData, {
      timeout: TimeoutRequestConfigurationEnum.FiveMinutesInMilliseconds
    });
  }

  getQuoteItem(
    request: GetQuoteItemRequest
  ): Promise<AxiosResponse<Result<GetQuoteItemResponse>, any>> {
    return api.get(`${baseRoute}/getQuoteItem`, { params: request });
  }

  reQuote(guidQuote: string): Promise<AxiosResponse<Result<PreQuoteResponse>>> {
    return api.post(`${baseRoute}/requote/${guidQuote}`);
  }

  addMissingItemsToCart(
    guidQuote: string
  ): Promise<AxiosResponse<Result<PreQuoteResponse>>> {
    return api.post(
      `${baseRoute}/addmissingitemstocart/${guidQuote}`,
      {},
      {
        timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
      }
    );
  }

  getClaimDetails(
    quoteGuid: string,
    guidScreen?: string
  ): Promise<AxiosResponse<Result<ClaimDetailsResponse>>> {
    return api.get(`${baseRoute}/getclaimdetail/`, {
      params: { quoteGuid: quoteGuid, guidScreen: guidScreen }
    });
  }
  removeQuoteItem(
    request: QuoteRemoveQuoteItemRequest
  ): Promise<AxiosResponse<Result<QuoteGetItemConditionsWrapper>>> {
    return api.delete(`${baseRoute}/removeQuoteItem`, { params: request });
  }
  getItemsToRequote(
    request: GetItemsToRequoteRequest
  ): Promise<AxiosResponse<Result<GetItemsToRequoteResponse>, any>> {
    return api.get(`${baseRoute}/getItemsToRequote`, { params: request });
  }
  requoteItems(
    request: RequoteRequest
  ): Promise<AxiosResponse<Result<QuoteGetItemConditionsWrapper>, any>> {
    return api.put(`${baseRoute}/requoteItems`, request, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }
  requoteNationalItems(
    request: RequoteNationalRequest
  ): Promise<AxiosResponse<Result<QuoteGetItemConditionsWrapper>, any>> {
    return api.post(`${baseRoute}/requoteitemsnational`, request, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }
  removeQuoteItems(
    request: RemoveQuoteItemRequest
  ): Promise<AxiosResponse<Result<QuoteGetItemConditionsWrapper>>> {
    return api.delete(`${baseRoute}/removeQuoteItems`, { data: request });
  }
  quoteXml(
    request: QuoteXmlRequest
  ): Promise<AxiosResponse<Result<QuoteXmlResponse>>> {
    return api.get(`${baseRoute}/partialquotexml`, {
      params: request,
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }
  saveXmlPaint(
    request: SaveXmlPaintRequest,
    file: File
  ): Promise<AxiosResponse<Result<string>>> {
    const formData = new FormData();
    formData.append('uploadFiles', file);

    const queryParams = new URLSearchParams(request as any).toString();
    const url = `${baseRoute}/saveXmlPaint?${queryParams}`;

    return api.post(url.toString(), formData, {
      timeout: TimeoutRequestConfigurationEnum.ThreeMinutesInMilliseconds
    });
  }

  showStripePaymentButton(
    guidQuote: string
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.get(`${baseRoute}/showstripebutton`, {
      params: { guidQuote }
    });
  }
  domainQuoteStatus(): Promise<AxiosResponse<Result<KeyValue[]>>> {
    return api.get(`${baseRoute}/domainquotestatus`);
  }
  buyNow(
    request: QuoteCheckoutRequest
  ): Promise<AxiosResponse<Result<QuoteCheckoutResponse>>> {
    return api.post(`${baseRoute}/paynow`, request);
  }
  updatePaymentPending(
    guidQuote: string
  ): Promise<AxiosResponse<Result<boolean>>> {
    return apiFetch.put(`${baseRoute}/updatepaymentpending`, { guidQuote });
  }

  changeQuoteToAwaitingRelease(
    guidQuote: string
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.post(
      `${baseRoute}/changequotetoawaitingrelease?guidQuote=${guidQuote}`
    );
  }

  domainPreQuoteType(): Promise<AxiosResponse<Result<KeyValue[]>>> {
    return api.get(`${baseRoute}/domainprequotetype`);
  }

  getQuoteManualByGuidClaim(
    guidClaim: string
  ): Promise<AxiosResponse<Result<QuoteManualChekoutResponse[]>>> {
    return api.get(`${baseRoute}/getquotemanualbyguidclaim`, {
      params: { guidClaim: guidClaim }
    });
  }

  getTotalLossByGuidQuote(
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteTotalLossResponse>>> {
    return api.get(`${baseRoute}/GetTotalLossByGuidQuote`, {
      params: { guidQuote: guidQuote }
    });
  }

  approveOrRejectParts(
    request: ApproveOrRejectQuoteRequest
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.post(
      `${baseRoute}/approval-or-reject-awaiting-approval`,
      request,
      {
        timeout: TimeoutRequestConfigurationEnum.FiveMinutesInMilliseconds
      }
    );
  }

  getDetailsAwaitingApproval = async (
    guidQuote: string
  ): Promise<AxiosResponse<Result<QuoteAwaitingApprovalResponse>>> => {
    return api.get(
      `${baseRoute}/detailAwaitingApproval?guidQuote=${guidQuote}`
    );
  };
  expireDeadlineAnswer(
    guidQuote: string
  ): Promise<AxiosResponse<Result<boolean>>> {
    return api.post(`${baseRoute}/expireDeadlineAnswer?guidQuote=${guidQuote}`);
  }
}
